import type { IResponseNumber } from 'entities/Phone/api/types'

export type IResponseProvider = {
  label: string
  value: string
}

export type IParamsOptInTypes = {
  filter: 'dlc' | 'toll-free'
}

export type IResponseCountryState = {
  id: string
  country_id: string
  name: string
}

export type IResponseTollFreeNumber = IResponseNumber & {
  toll_free_verification_errors: string[]
  individual_toll_free_profile: IResponseTollFree
  formatted_number: string
}

export type IResponseTollFree = {
  id: number
  organization_id: number
  title: string | null
  monthly_volume: number
  use_case_description: string | null
  use_case_id: number | null
  sample_message_1: string | null
  sample_message_2: string | null
  completed: boolean
  created_at: string
  updated_at: string
  explanation_description: string | null
  use_case: {
    id: number
    title: string
    description: string
    sample_message_1: string
    sample_message_2: string
  } | null
  opt_in_methods: {
    id: number
    key: string
    title: string
    description: string | null
  }[]
  opt_in_data: {
    id: number
    opt_in_method_id: number
    description: string
    files: IResponseTollFreeOptInUpload[]
    online_form_url: string | null
    online_form_published: boolean
  }[]
  is_parent: boolean
  step: number
  numbers: IResponseTollFreeNumber[]
  privacy_policy_url?: string
  terms_url?: string
}

export type IResponseOrganizationTollFreeUpdate = {
  business_information: IResponseBusinessInformation
  toll_free_profile: IResponseTollFree
}

export type IResponseOrganizationTollFree = {
  data: IResponseTollFree[]
}

export type IResponseOrganizationTollFreeFormFields = {
  monthly_values: number[]
  use_cases: Array<{ id: number; title: string }>
}

export type IResponseComplianceSummary = {
  vetting_score: null
  approved_campaigns: null
  compliance_provider: null
  services_data: null
  ten_dlc_limits_reached: null
  ten_dlc_plan: null
  organization_id: number | null
  has_tax_id: boolean | null
  target_country: ITargetCountry | null
  numbers_type: INumberType | null
  banners_closed: boolean
  tax_id_banner_closed: boolean
}

export type IResponseEinItem = {
  city: string
  ein: string
  ein_formatted: string
  name: string
  state: string
  street1: string
  street2: string
  zip: string
}

export type IResponseGetEinData = {
  data: IResponseEinItem[]
}

export type ICompanyLocation = 'US' | 'CA'

export type IIrsEinDocument = {
  business_information_id: number
  created_at: string
  extension: string
  id: number
  size: string
  source_url: string
  title: string
  updated_at: string
}

export type IRegionOfOperation = {
  id: number
  name: string
}

export type IParamsUpdateTollFreeBusinessInfo = {
  registration_type_id: number
  business_name: string
  ein: string
  street_address_1: string
  street_address_2?: string
  city: string
  website_url: string
  without_website: boolean
  state_id: string
  country: string
  postal_code: string
  should_update_stripe_customer?: boolean
  social_media_url: string | null
}

export type IParamUpdateTollFreeContactInfo = {
  first_name: string
  last_name: string
  email: string
  phone_number: string
}

export type IParamUpdateTollFreeUseCase = {
  monthly_volume: string
  use_case_id: number
  use_case_description: string
  sample_message_1: string
  privacy_policy_url: string
  terms_url: string
}

type ITollFreeOptInMethodData = {
  relatable_service_type: string
  opt_in_method_id: number
}

export type IParamUpdateTollFreeOptInMethod = {
  opt_in_data: ITollFreeOptInMethodData[]
}

export type IParamUpdateTollFreeOptInConsent = {
  opt_in_method_id: number
  online_form_url: string | null
  no_published_online_from: boolean | null
}

export type IParamsResubmitTollFree = {
  profile_id: number
  number_id: number
}

export type IResponseBusinessInformation = {
  business_name: string
  business_type_id: number | null
  business_type: {
    id: number
    name: string
  } | null
  industry: {
    id: number
    name: string
  }
  city: string
  country: ICompanyLocation
  organization_id: number
  industry_id: number
  social_media_url: string | null
  regions_of_operation: IRegionOfOperation[]
  reg_progress_status: string
  registration_number?: string
  ein?: string
  irs_ein_document: IIrsEinDocument | null
  registration_type_id: number
  state_id: string
  street_address_1: string
  street_address_2: string | null
  website_url: string | null
  zip_code?: string
  postal_code?: string
  contact_person: IResponsePointOfContact
}

export interface IParamsBusinessInformation
  extends Omit<
    IResponseBusinessInformation,
    'regions_of_operation' | 'business_type' | 'industry' | 'contact_person'
  > {
  regions_of_operation: number[]
}

export type ITargetCountry = 'US' | 'CA' | 'ALL'
export type INumberType = 'toll_free' | 'local' | 'all'

export type IParamsSaveComplianceSummary = {
  target_country: ITargetCountry
  have_tax_ein: boolean
  numbers_type: INumberType
}

export type IParamsSetBpBusinessInformation = {
  business_information: IParamsBusinessInformation
  step: number
}

export type IParamsSetBpBusinessInformationLocation = {
  business_information: {
    country: ICompanyLocation
  }
  step: number
}

export type IResponseBusinessProfileStatus = 'under_review' | 'approved' | 'failed' | 'in_review'

export type IResponseLocalNumbersStatus =
  | 'in_progress'
  | 'pending_review'
  | 'in_carrier_review'
  | 'approved'
  | 'failed'

export type IResponseTwilioServiceError = {
  error_message: string
}

export type IResponseBusinessProfile = {
  status: IResponseBusinessProfileStatus
  twilio_service_errors: IResponseTwilioServiceError[]
  created_at: string
  submitted_at: string | null
}

export type IResponseBusinessLocation = null

export type IResponseBusinessTerms = {
  created_at: string
}

export type IResponseOptInType = {
  id: number
  key: string
  title: string
}

export type IResponseOrganizationBusinessProfile = {
  businessInformation: IResponseBusinessInformation | null
  businessLocation: IResponseBusinessLocation | null
  businessProfile: IResponseBusinessProfile | null
  pointsOfContact: IResponsePointOfContact[]
  terms: IResponseBusinessTerms | null
}

export type IResponsePointOfContact = {
  first_name: string
  last_name: string
  email: string
  business_title: string
  job_position_id: number
  phone_number: string
  contacts_confirmed: true
  company_profile_fields: {
    first_name: string
    last_name: string
    phone_number: string
  }
  job_position: {
    id: number
    name: string
  }
}

export type IParamsSetBpBusinessPointsOfContact = {
  contacts_confirmed: boolean
  points_of_contact: {
    business_title: string
    email: string
    first_name: string
    job_position_id: string
    last_name: string
    phone_number: string
  }[]
  step: number
}

export type IParamsSetBpBusinessTerms = {
  terms_accepted: boolean
  step: number
}

export type IResponseUseCase = {
  description: string
  useCase: string
  useCaseTitle: string
}

export type IResponseOnlineFormUrlCheck = {
  domain_check: boolean
  reachable: boolean
}

export type IOnlineFormUrlCheck = {
  onlineFormUrl: string
}

export type IParamsUpdateComplianceServicesCampaign = {
  step: number
  useCaseData?: IResponseUseCase
  vendorKey?: string
  campaignId?: number | null
  sampleMessagesData?: IResponseSampleMessagesData
  optInMethods?: string[]
  optInData?: IResponseOptInData
}

export type IParamsSetCampaignPackageData = {
  step: number
  campaignId: number
  planType: string
  businessType: string
  stockExchange?: string
  stockTickerSymbol?: string
  businessVerificationEmail?: string
}

export type ISampleMessagesData = {
  sampleMessage1: string
  sampleMessage2: string
  includeAgeGatedContent: boolean
  includeEmbeddedLinks: boolean
  includePhoneNumbers: boolean
}

export type IResponseSampleMessagesData = ISampleMessagesData & {
  privacyPolicyUrl: string | null
  termsUrl: string | null
}

export enum EnumOptInMethodTypes {
  onlineForm = 'ONLINE_FORM',
  paperForm = 'PAPER_FORM',
  text = 'TEXT',
  qrCode = 'QR_CODE',
}

export type IOptInMethodNames = {
  title: string
  value: EnumOptInMethodTypes
}

export type IResponseOptInData = {
  optInMethod: EnumOptInMethodTypes
  optInImages: IResponseOptInUpload[]
  onlineFormUrl: string | null
  noPublishedOnlineFormUrl: boolean | null
}

export type IResponseTollFreeOptInData = {
  optInMethod: EnumOptInMethodTypes
  optInImages: IResponseTollFreeOptInUpload[]
  onlineFormUrl: string | null
  noPublishedOnlineFormUrl: boolean | null
}

export type IRegistrationError = {
  id: number
  message: string
  rejection_source: 'admin' | 'tcr' | 'twilio' | 'subscription_cancellation'
}

export type IResponseComplianceServicesCampaignData = {
  useCaseData: IResponseUseCase | null
  sampleMessagesData: IResponseSampleMessagesData | null
  optInData: IResponseOptInData[] | null
  messageFlow: string | null
  registrationFormStep: number | null
}

export type IResponseComplianceServicesCampaign = {
  id: number
  brand_id: number | null
  data: IResponseComplianceServicesCampaignData
  registration_errors: IRegistrationError[]
  organization_id: number
  number_vendor_key: string
  status: IResponseLocalNumbersStatus
  create_at: string
  submitted_at: string | null
  updated_at: string
  vendor_id: null
}

export enum IPlaneTypes {
  LOW_VOLUME_STANDARD = 'LOW_VOLUME_STANDARD',
  STANDARD = 'STANDARD',
}

export enum IBusinessType {
  PUBLIC_PROFIT = 'PUBLIC_PROFIT',
  PRIVATE_PROFIT = 'PRIVATE_PROFIT',
}

export type IVettingType = {
  id: number
  organization_id: number
  brand_id: number
  tcr_id: string
  token: string | null
  score: number
  status: string
}

export type IResponseComplianceServicesBrand = {
  id: number
  organization_id: number
  vendor_id: string | null
  data: {
    firstName: string | null
    lastName: string | null
    displayName: string | null
    companyName: string | null
    phone: string | null
    street: string | null
    city: string | null
    state: string | null
    postalCode: string | null
    country: string | null
    email: string | null
    brandRelationship: string | null
    vertical: string | null
    mock: false | null
    ein: string | null
    einIssuingCountry: null
    entityType: IBusinessType | null
    website: string | null
    socialMediaProfile: string | null
    stockSymbol?: string | null
    stockExchange?: string | null
    business_type: IResponseBusinessInformation['business_type'] | null
    industry: IResponseBusinessInformation['industry'] | null
    businessContactEmail: null
    website_url: string | null
    regions_of_operation: IRegionOfOperation[]
  } | null
  business_email_verification?: {
    email: string
    status: string
  }
  plan_type: IPlaneTypes
  status: IResponseLocalNumbersStatus
  registration_errors: IRegistrationError[]
  compliance_provider_key: string
  create_at: string
  submitted_at: string | null
  vetting?: IVettingType
}

export type IResponseGetComplianceServicesList = {
  campaigns: IResponseComplianceServicesCampaign[]
  brand: IResponseComplianceServicesBrand | null
  vetting: []
}

export type IResponseComplianceServicesBrandPackage = {
  brand: IResponseComplianceServicesBrand
}

export type IParamsSubmitLocalNumberApplication = {
  ten_dlc_registration_submitted: boolean
  ten_dlc_plan: string
}

export type IParamsTollFreeDeleteAttachment = {
  opt_in_file_id: string
}

export type IResponseTollFreeOptInUpload = {
  source_url: string
  name: string
  size: string
  id: string
}

export type IResponseStockExchanges = {
  title: string
  value: string
}

export type IParamsDeleteAttachment = {
  sourceUrl: string
  uid: string
}

export type IResponseOptInUpload = {
  sourceUrl: string
  name: string
  size: string
  uid: string
}

export type IParamsSetCampaignTollFreeOptInFiles = {
  file: File
  opt_in_method_id: number
  profile_id: number
  relatable_service_type: number
}

export type IParamsGenerateAIDesc = {
  company_name: string
  website: string
}

export type IParamsBrandGenerateAIDesc = {
  companyName: string
  website?: string
}

export type IParamsGenerate10DLCAIDesc = {
  brand: IParamsBrandGenerateAIDesc
  useCaseData: IResponseUseCase
}

export type IResponseGenerate10DLCAIDesc = {
  description: string
}

export type IResponseGenerateAI = {
  prompt_type: 'toll_free_verification_description' | 'toll_free_verification_sample_message'
  result_message: string
  result_message_length: number
}

export type IParamsGenerateAIMessage = {
  company_name: string
  use_case: string
  description: string
  website: string
}

export type IParamsGetFreeTollFreeNumbers = {
  country: string
  toll_free: string
}

export type IResponseFreeTollFreeNumber = {
  country: string
  formatted: string
  number: string
}

export type IParamsAnalyze10DLCAI = {
  brand?: IParamsBrandGenerateAIDesc
  useCaseData?: IResponseUseCase | null
  sampleMessagesData?: ISampleMessagesData | null
  optInData?: IResponseOptInData[] | null
  messageFlow?: string | null
  privacyPolicyUrl?: string
  termsUrl?: string
}

export type IParamsGenerate10DLCAIMessage = {
  brand: IParamsBrandGenerateAIDesc
  useCaseData?: IResponseUseCase | null
  sampleMessagesData: IResponseSampleMessagesData
}

export type IResponseGenerate10DLCAIMessage = {
  sampleMessage1: string
  sampleMessage2: string
}

export type IResponseAnalyze10DLCAISuggestion = {
  field: string
  severity: string
  text: string
}

export type IResponseAnalyze10DLCAI = {
  suggestions: IResponseAnalyze10DLCAISuggestion[]
}
export type IParamsAddTollFreeNumberToExistingInbox = {
  number: string
  number_vendor_id: number
  vendor_id: string
  name: string
  members: unknown | null
  owner: {
    id: number | null
  }
  is_mobile: boolean | null
  payment_method?: string | null
}

export type IParamsAddTollFreeNumberWithNewInbox = {
  number: string
  number_vendor_id: number
  vendor_id: string
  name: string
  members: string[]
  owner: {
    id: number
  }
  is_mobile: boolean | null
  payment_method?: string | null
}

export type IParamsSubmitTollFreeForReview = {
  numbers: number[]
  additional_numbers: number[]
}

export type IResponseNumberVendor = {
  id: number
  key: string
  name?: string
}

export enum ICountriesEnum {
  GB = 'GB',
  US = 'US',
  CA = 'CA',
  AU = 'AU',
  OTHER = 'OTHER',
}

export type IInternationalBusinessInformation = {
  business_name?: string
  registration_number?: string
  ein?: string
  city?: string
  country?: ICountriesEnum | null
  social_media_url?: string | null
  state_id?: string
  street_address_1?: string
  street_address_2?: string | null
  website_url?: string | null
  zip_code?: string
  postal_code?: string
  postal_v_code?: string
}

export type IResponseInternationalBusinessInformation = {
  businessRegistrationNumber: string
  businessWebsiteUrl: string
  hasBusinessWebsite: boolean
  legalBusinessName: string
  registrationAuthority: string
}

export type IResponseInternationalBusinessAddress = {
  businessAddress: string
  businessAddress2: string | null
  city: string
  country: ICountriesEnum
  state: string
  zipCode: string
}

export type IResponseInternationalAuthorizedRepresentative = {
  businessEmail: string
  firstName: string
  lastName: string
  phoneNumber: string
}

export type IResponseInternationalStatus =
  | 'in_progress'
  | 'pending'
  | 'approved'
  | 'in_carrier_review'
  | 'under_review'
  | 'created'
  | 'failed'

export type IEndUserType = 'business' | 'individual'

export type IInternationalNumberType = 'mobile' | 'landline' | 'local' | 'toll-free' | 'short-code'

export type IResponseInternationalRegulation = {
  id: number
  country: string
  endUserType: IEndUserType
  numberType: IInternationalNumberType
  title: string
  sid: string
  documentUploadRequired: boolean
}

export type IResponseInternationalRegulatoryBundleItem = {
  id: number
  organizationId: number
  vendorId: string | null
  status: IResponseInternationalStatus
  step: number
  businessData: IResponseInternationalBusinessInformation | null
  addressData: IResponseInternationalBusinessAddress | null
  authorizedRepresentativeData: IResponseInternationalAuthorizedRepresentative | null
  createdAt: string
  updatedAt: string
  submittedAt: string | null
  regulation: IResponseInternationalRegulation | null
  registrationErrors: IRegistrationError[] | null
}

export type IResponseInternationalRegulatoryBundle =
  | { data: IResponseInternationalRegulatoryBundleItem[]; message: string }
  | { message: string; errors: string }
  | { message: string }

export type IParamsGetInternationalRegulatoryBundle = {
  country: string
  endUserType: IEndUserType
  numberType: IInternationalNumberType
}
export type IResponseInternationalComplianceRegulationsItem = {
  id: number
  country: string
  end_user_type: IEndUserType
  number_type: IInternationalNumberType
  title: string
  sid: string
  document_upload_required: boolean
  createdAt: string
  updatedAt: string
}

export type IResponseInternationalComplianceRegulations =
  | { data: IResponseInternationalComplianceRegulationsItem[] }
  | {
      message: string
    }
  | {
      errors: string
    }

export type IParamsSubmitBusinessDataInternationalRegulatory = {
  regulationId: number
  legalBusinessName: string
  registrationAuthority: string | null
  businessRegistrationNumber: string
  businessWebsiteUrl: string
  hasBusinessWebsite: boolean | null
  bundleId: number | null
}

export type IParamsSubmitAddressDataInternationalRegulatory = {
  bundleId: number
  country: string
  businessAddress: string
  businessAddress2: string | null
  city: string
  state: string
  zipCode: string
}

export type IResponseSubmitDataInternationalRegulatory = {
  data: {
    id: number
    organizationId: number
    vendorId: string
    status: IResponseInternationalStatus
    step: number
    businessData: IInternationalBusinessInformation | null
    addressData: IResponseInternationalBusinessAddress | null
    authorizedRepresentativeData: IResponseInternationalAuthorizedRepresentative | null
    createdAt: string
    updatedAt: string
    submittedAt: string
    regulation: IResponseInternationalRegulation | null
  }
  message?: string
}

export type IParamsSubmitAuthorizedRepresentativeDataInternationalRegulatory = {
  bundleId: number
  firstName: string
  lastName: string
  businessEmail: string
  phoneNumber: string
}

export type IParamsSubmitAInternationalRegulatory = {
  bundleId: number
}

export type IError = {
  [key: string]: string | string[]
}
